import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Plus, Trash2, X, CheckCircle, RotateCcw, Filter, Lock, AlertCircle, Copy, Edit, Search, HelpCircle, Info, ArrowUp, ArrowDown, ArrowUpDown, ChevronsUpDown, AlertTriangle } from 'lucide-react';
import { CategoryOption } from '../../lib/categories/categories';
import { AccountType, NWCRole } from '../../types'; 
import { templateApi, authApi, projectApi } from '../../lib/api/authApi';
import { CategoryTemplate } from '../../types';
import { detectNWCRole, isDefinitionalAdjustment, isTradeWorkingCapital, getNWCRoleColorClass } from '../../lib/financial/nwcUtils';
import NWCRoleSelector from './NWCRoleSelector';
import TemplateManagement from './TemplateManagement';
import AddCategoryModal from './AddCategoryModal';
import { detectEBITDARole, getEBITDARoleColorClass, getEBITDADisplayLabel } from '../../lib/financial/ebitdaUtils';
import EBITDARoleSelector from './EBITDARoleSelector';
import { EBITDARoleLegend } from './EBITDARoleLegend';
import CollapsibleLegend from './CollapsibleLegend';

interface CategoriesManagementProps {
  onUpdateCategories: (categories: CategoryOption[]) => void;
  onTemplateSelect?: (template: CategoryTemplate) => void;
  activeTemplate: CategoryTemplate | null;
}

interface NotificationProps {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
}

// Filter state interface
interface FilterState {
  value: string[];
  enabled: boolean;
}

type Filters = {
  label: FilterState;
  type: FilterState;
  fsType: FilterState;
};

// Sort configuration
type SortField = 'label' | 'type' | 'fsType' | 'nwcRole' | 'ebitdaRole' | 'tagsInclude' | 'tagsExclude' | 'originalOrder';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

// Financial statement order weights
const financialStatementOrder: Record<string, number> = {
  // Balance Sheet (Assets)
  'current-asset': 10,
  'non-current-asset': 20,
  // Balance Sheet (Liabilities)
  'current-liability': 30,
  'non-current-liability': 40,
  // Balance Sheet (Equity)
  'equity': 50,
  'contra-equity': 51,
  // Income Statement
  'revenue': 60,
  'contra-revenue': 61,
  'cogs': 70,
  'contra-cogs': 71,
  'operating-expense': 80,
  'interest-expense': 90,
  'income-tax-expense': 100,
  'other-expense': 110,
  'n/a': 999
};

// Check if account type is eligible for EBITDA role
const isEligibleForEBITDARole = (accountType: AccountType): boolean => {
  return ['interest-expense', 'income-tax-expense', 'operating-expense'].includes(accountType);
};

// Check if account type is eligible for NWC role
const isEligibleForNWCRole = (fsType: string): boolean => {
  return fsType === 'balance-sheet';
};

// Notification component
const Notification: React.FC<NotificationProps> = ({ message, type, onClose }) => {
  // Auto-close notification after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [onClose]);
  
  return (
    <div className={`fixed bottom-4 right-4 flex items-center p-4 rounded-md shadow-lg ${
      type === 'success' ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'
    }`}>
      {type === 'success' && <CheckCircle className="h-5 w-5 text-green-500 mr-2" />}
      <span className={type === 'success' ? 'text-green-700' : 'text-red-700'}>{message}</span>
      <button onClick={onClose} className="ml-4 text-gray-400 hover:text-gray-600">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Multi-select combobox component for filtering
function ComboboxSelect({ 
  value, 
  onChange, 
  options, 
  placeholder,
  onToggle = () => {} 
}: { 
  value: string[];
  onChange: (value: string[]) => void;
  options: string[];
  placeholder: string;
  onToggle?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside of the component
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onToggle();
    }
  };

  // Filter options based on input
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleSelect = (selectedOption: string) => {
    const newValue = value.includes(selectedOption)
      ? value.filter(v => v !== selectedOption)
      : [...value, selectedOption];
    onChange(newValue);
    setInputValue('');
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative">
        <div className="min-h-[36px] w-full border rounded px-2 py-1 text-sm flex flex-wrap gap-1">
          {/* Selected items */}
          {value.map((item) => (
            <span
              key={item}
              className="bg-blue-100 text-blue-800 rounded px-1 flex items-center gap-1"
            >
              {item}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(value.filter(v => v !== item));
                }}
                className="hover:text-blue-600 ml-1"
              >
                ×
              </button>
            </span>
          ))}
          
          {/* Input field */}
          <input
            type="text"
            className="flex-1 outline-none min-w-[60px] bg-transparent"
            placeholder={value.length === 0 ? placeholder : ''}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className="absolute inset-y-0 right-0 flex items-center pr-2"
        >
          <ChevronsUpDown className="h-4 w-4 text-gray-400" />
        </button>
      </div>
      {isOpen && (
        <div className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg border z-50">
          {filteredOptions.length === 0 ? (
            <div className="relative px-2 py-1.5 text-sm text-gray-500">
              No options found
            </div>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option}
                className="relative flex cursor-pointer select-none items-center px-2 py-1.5 text-sm hover:bg-gray-100"
                onClick={() => handleSelect(option)}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={value.includes(option)}
                    onChange={() => {}}
                    className="rounded border-gray-300"
                  />
                  <span className="truncate">{option}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function CategoriesManagement({ 
  onUpdateCategories,
  onTemplateSelect,
  activeTemplate
}: CategoriesManagementProps) {
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [templates, setTemplates] = useState<CategoryTemplate[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<CategoryTemplate | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [originalCategoryState, setOriginalCategoryState] = useState<CategoryOption | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showLegends, setShowLegends] = useState(false);
  
  // Add notification state
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({ show: false, message: '', type: 'success' });
  
  // Add state to track newly added categories
  const [newlyAddedCategory, setNewlyAddedCategory] = useState<string | null>(null);
  
  // Updated sorting and filtering states to match Chart of Accounts
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'originalOrder', direction: 'asc' });
  const [filters, setFilters] = useState<Filters>({
    label: { value: [], enabled: false },
    type: { value: [], enabled: false },
    fsType: { value: [], enabled: false }
  });
  const [activeFilterColumn, setActiveFilterColumn] = useState<keyof Filters | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  // Track active filters count
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(filter => filter.enabled).length;
  }, [filters]);

  // Reset filters button state
  const [isFilterActive, setIsFilterActive] = useState(false);

  const accountTypes: AccountType[] = [
    'current-asset',
    'non-current-asset',
    'current-liability',
    'non-current-liability',
    'equity',
    'contra-equity',
    'revenue',
    'contra-revenue',
    'cogs',
    'contra-cogs',
    'operating-expense',
    'interest-expense',
    'income-tax-expense',
    'other-expense',
    'n/a'
  ];

  useEffect(() => {
    const init = async () => {
      try {
        const sessionValidation = await authApi.validateSession();
        
        if (!sessionValidation.valid) {
          console.log('No authenticated user, skipping initialization');
          return;
        }

        await fetchTemplates();
      } catch (error) {
        console.error('Error initializing:', error);
        // Only show alert if it's not an authentication error
        if (!error.message?.includes('No authenticated user')) {
          alert('Failed to initialize database connection');
        }
      }
    };
    init();
  }, []);

  // Set the editing template to match active template initially
  useEffect(() => {
    if (activeTemplate && !editingTemplate) {
      setEditingTemplate(activeTemplate);
      setCategories(activeTemplate.categories);
    }
  }, [activeTemplate, editingTemplate]);

  // Check if any filters are active
  useEffect(() => {
    setIsFilterActive(activeFilterCount > 0);
  }, [activeFilterCount]);

  // Effect to highlight newly added category for a few seconds
  useEffect(() => {
    if (newlyAddedCategory) {
      const timer = setTimeout(() => {
        setNewlyAddedCategory(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [newlyAddedCategory]);

  // Add CSS styles for sticky header and columns
  useEffect(() => {
    // Add a style tag to ensure proper styling for sticky elements
    const styleTag = document.createElement('style');
    styleTag.textContent = `
    .sticky-table-wrapper {
      overflow: auto;
      max-height: calc(100vh - 320px);
      border: 1px solid #e5e7eb;
      border-radius: 6px;
    }
    
    .sticky-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
    }
    
    .sticky-table td,
    .sticky-table th {
      border-right: 1px solid #e5e7eb;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .sticky-table tbody tr:nth-child(even) {
      background-color: #f9fafb;
    }
    
    .sticky-table tbody tr:nth-child(odd) {
      background-color: white;
    }
    
    .sticky-table tbody tr:hover {
      background-color: #f3f4f6 !important;
    }
    
    .sticky-header-cell {
      position: sticky;
      top: 0;
      z-index: 30;
      background: white;
      box-shadow: 0 1px 0 0 #e5e7eb;
    }
    
    .sticky-column {
      position: sticky;
      background: white;
      z-index: 20;
      border-right: 1px solid #e5e7eb;
    }
    
    .sticky-header-cell.sticky-column {
      z-index: 40;
    }
    
    .sticky-category-name {
      left: 0;
      width: 250px;
      min-width: 250px;
    }
    
    .actions-column {
      width: 100px;
      min-width: 100px;
    }
    
    /* Helper text in EBITDA column - allow wrapping */
    .ebitda-helper-text {
      white-space: normal;
      font-size: 0.75rem;
      line-height: 1.2;
      color: #6b7280;
      display: block;
      max-width: 140px;
    }
    
    /* Custom scrollbar styling for better visibility */
    .sticky-table-wrapper::-webkit-scrollbar {
      height: 8px;
      width: 8px;
    }
    
    .sticky-table-wrapper::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }
    
    .sticky-table-wrapper::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;
    }
    
    .sticky-table-wrapper::-webkit-scrollbar-thumb:hover {
      background: #a1a1a1;
    }
    
    /* Styling for disabled/non-applicable role cells */
    .role-not-applicable {
      color: #9ca3af;
      font-style: italic;
      font-size: 0.75rem;
      padding: 4px 8px;
      background-color: #f9fafb;
      border-radius: 4px;
      border: 1px dashed #e5e7eb;
    }
    `;
    document.head.appendChild(styleTag);
    
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  const fetchTemplates = async () => {
    try {
      // Check if user is still authenticated before making API call
      const sessionValidation = await authApi.validateSession();
      if (!sessionValidation.valid) {
        console.log('User not authenticated, skipping template fetch');
        return;
      }
      
      const data = await templateApi.getAll();
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Only show alert if it's not an authentication error
      if (!error.message?.includes('Request failed')) {
        alert('Failed to load category templates');
      }
    }
  };

  // This handles making a template the active one (system-wide)
  // AND sets it as the current editing template
  const handleSetActiveTemplate = (template: CategoryTemplate) => {
    if (onTemplateSelect) {
      onTemplateSelect(template);
    }
    
    // Also update the editing template to match the newly activated template
    setEditingTemplate(template);
    setCategories(template.categories);
    
    // Reset sorting and filtering when changing templates
    resetSortAndFilter();
  };

  // This handles selecting a template for editing only (doesn't change active)
  const handleSelectTemplateForEditing = (template: CategoryTemplate) => {
    setEditingTemplate(template);
    setCategories(template.categories);
    
    // Reset sorting and filtering when changing templates
    resetSortAndFilter();
  };

  const handleTemplateCopy = async (templateToCopy: Partial<CategoryTemplate>) => {
    try {
      // Create new template object without the id
      const { id, ...newTemplateData } = templateToCopy;
      
      const data = await templateApi.create(newTemplateData);
  
      await fetchTemplates();
      
      // Select the new template for editing
      setEditingTemplate(data);
      setCategories(data.categories);
      
      // Reset sorting and filtering when changing templates
      resetSortAndFilter();
      
      // Show success notification
      setNotification({
        show: true,
        message: 'Template copied successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error copying template:', error);
      alert('Failed to copy template');
    }
  };

  const handleTemplateDelete = async (templateId: string) => {
    try {
      // Check if template is in use by any projects
      try {
        const projects = await projectApi.getAll();
        const projectsUsingTemplate = projects.filter(p => p.template_id === templateId);
        
        if (projectsUsingTemplate.length > 0) {
          alert('Cannot delete template because it is being used by one or more projects.');
          return;
        }
      } catch (projectError) {
        console.error('Error checking projects:', projectError);
        // Continue with delete if project check fails
      }

      await templateApi.delete(templateId);

      // If we deleted the template we were editing, switch to active template
      if (editingTemplate?.id === templateId) {
        setEditingTemplate(activeTemplate);
        if (activeTemplate) {
          setCategories(activeTemplate.categories);
          
          // Reset sorting and filtering when changing templates
          resetSortAndFilter();
        }
      }

      // Refresh templates list
      await fetchTemplates();
      
      // Show success notification
      setNotification({
        show: true,
        message: 'Template deleted successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template. Please try again.');
    }
  };

  const handleCreateTemplate = async (name: string) => {
    try {
      // Validate session first
      const sessionValidation = await authApi.validateSession();
      if (!sessionValidation.valid) {
        alert('You must be logged in to create a template');
        return;
      }
      
      // Create a template with the provided name
      const newTemplate = {
        name: name, // Use the name provided by the user
        categories: [], // You might want to add some starter categories
        is_default: false
      };
      
      const data = await templateApi.create(newTemplate);
      
      // Refresh templates and select the new one for editing
      await fetchTemplates();
      if (data) {
        setEditingTemplate(data);
        setCategories(data.categories);
        
        // Reset sorting and filtering when changing templates
        resetSortAndFilter();
        
        // Show success notification
        setNotification({
          show: true,
          message: 'New template created successfully',
          type: 'success'
        });
      }
      
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Failed to create template. Please try again.');
    }
  };

  // Add this new method for renaming templates
  const handleRenameTemplate = async (templateId: string, newName: string) => {
    try {
      // Check if it's the default template
      const template = templates.find(t => t.id === templateId);
      if (!template || template.is_default) {
        setNotification({
          show: true,
          message: 'Cannot rename default template',
          type: 'error'
        });
        return;
      }
      
      await templateApi.update(templateId, { 
        name: newName,
        updated_at: new Date().toISOString()
      });
      
      // Refresh templates
      await fetchTemplates();
      
      // Update the editing template if it was renamed
      if (editingTemplate?.id === templateId) {
        setEditingTemplate({
          ...editingTemplate,
          name: newName
        });
      }
      
      // Show success notification
      setNotification({
        show: true,
        message: 'Template renamed successfully',
        type: 'success'
      });
      
    } catch (error) {
      console.error('Error renaming template:', error);
      throw error;
    }
  };

  const getFSType = (type: AccountType): 'balance-sheet' | 'income-statement' => {
    const balanceSheetTypes = [
      'current-asset',
      'non-current-asset',
      'current-liability',
      'non-current-liability',
      'equity',
      'contra-equity'
    ];
    return balanceSheetTypes.includes(type) ? 'balance-sheet' : 'income-statement';
  };

  // Component for displaying the NWC Role Legend
  const NWCRoleLegend = () => {
    return (
      <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-2">NWC Role Legend</h3>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 mr-2">
              Cash / Short-term Debt / Debt-Like
            </span>
            <span className="text-xs text-gray-500">Definitional Adjustments (excluded from Trade Working Capital)</span>
          </div>
          
          <div className="flex items-center">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
              AR / Inventory / AP
            </span>
            <span className="text-xs text-gray-500">Trade Working Capital Components</span>
          </div>
        </div>
      </div>
    );
  };

  // Sorting function for categories in financial statement order
  const sortCategoriesByFinancialOrder = (a: CategoryOption, b: CategoryOption) => {
    const orderA = financialStatementOrder[a.type] || 999;
    const orderB = financialStatementOrder[b.type] || 999;
    return orderA - orderB;
  };

  // Updated to add original order when adding categories
  const handleAddCategory = async (newCategory: CategoryOption) => {
    if (!editingTemplate) return;
    
    // Check if editing a default template
    if (editingTemplate.is_default) {
      setNotification({
        show: true,
        message: 'Cannot modify the default template',
        type: 'error'
      });
      return;
    }
    
    try {
      // Add the new category in the proper financial statement order
      const updatedCategories = [...categories, newCategory].sort(sortCategoriesByFinancialOrder);
      
      await templateApi.update(editingTemplate.id, { 
        categories: updatedCategories,
        updated_at: new Date().toISOString()
      });
  
      setCategories(updatedCategories);
      onUpdateCategories(updatedCategories);
      setIsAddModalOpen(false);
      
      // Set the newly added category for highlighting
      setNewlyAddedCategory(newCategory.value);
      
      // Show success notification
      setNotification({
        show: true,
        message: 'Category added successfully',
        type: 'success'
      });
      
      // Reset sort and filter to make the new category visible
      resetSortAndFilter();
    } catch (error) {
      console.error('Error adding category:', error);
      setNotification({
        show: true,
        message: 'Failed to add category',
        type: 'error'
      });
    }
  };

  // Updated sorting to match Chart of Accounts
  const handleSort = (field: SortField) => {
    setSortConfig(prevConfig => {
      if (prevConfig.field === field) {
        // Toggle direction if clicking on the same field
        return { 
          field, 
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc' 
        };
      }
      // Default to ascending order for new field
      return { field, direction: 'asc' };
    });
  };

  // Reset to original order
  const resetToOriginalOrder = () => {
    setSortConfig({ field: 'originalOrder', direction: 'asc' });
  };

  // Reset all sorting and filtering
  const resetSortAndFilter = () => {
    setSortConfig({ field: 'originalOrder', direction: 'asc' });
    setFilters({
      label: { value: [], enabled: false },
      type: { value: [], enabled: false },
      fsType: { value: [], enabled: false }
    });
    setActiveFilterColumn(null);
  };

  // Format category type for display
  const formatCategoryType = (type: string): string => {
    return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  // Updated filtering to match Chart of Accounts
  const handleFilterChange = (field: keyof typeof filters, value: string[]) => {
    setFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], value }
    }));
  };

  const handleFilterToggle = (field: keyof typeof filters) => {
    setFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], enabled: !prev[field].enabled }
    }));
  };

  const clearFilters = () => {
    setFilters({
      label: { value: [], enabled: false },
      type: { value: [], enabled: false },
      fsType: { value: [], enabled: false }
    });
    setActiveFilterColumn(null);
  };

  // New function to render sort icons consistent with Chart of Accounts
  const renderSortIcon = (field: SortField) => {
    // Add explicit style to ensure fixed dimensions for all icons
    const iconStyle = { width: '16px', height: '16px', minWidth: '16px', minHeight: '16px' };
    
    if (sortConfig.field !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" style={iconStyle} />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" style={iconStyle} />
      : <ArrowDown className="w-4 h-4 text-blue-600" style={iconStyle} />;
  };

  // Apply sorting and filtering to categories
  const filteredAndSortedCategories = useMemo(() => {
    // Add originalOrder to categories array
    const categoriesWithOrder = categories.map((category, index) => ({
      ...category,
      originalOrder: index
    }));
    
    // First, apply filters
    let result = [...categoriesWithOrder];
    
    // Category name filter
    if (filters.label.enabled && filters.label.value.length > 0) {
      result = result.filter(cat => 
        filters.label.value.includes(cat.label)
      );
    }
    
    // Category type filter
    if (filters.type.enabled && filters.type.value.length > 0) {
      result = result.filter(cat => 
        filters.type.value.includes(formatCategoryType(cat.type))
      );
    }
    
    // Financial statement type filter
    if (filters.fsType.enabled && filters.fsType.value.length > 0) {
      result = result.filter(cat => {
        const fsTypeDisplay = cat.fsType === 'balance-sheet' ? 'Balance Sheet' : 'Income Statement';
        return filters.fsType.value.includes(fsTypeDisplay);
      });
    }
    
    // Then apply sorting
    return [...result].sort((a, b) => {
      const { field, direction } = sortConfig;
      
      // Handle originalOrder special case
      if (field === 'originalOrder') {
        return direction === 'asc' 
          ? a.originalOrder - b.originalOrder 
          : b.originalOrder - a.originalOrder;
      }
      
      if (field === 'label') {
        return direction === 'asc'
          ? a.label.localeCompare(b.label)
          : b.label.localeCompare(a.label);
      } else if (field === 'type') {
        return direction === 'asc'
          ? a.type.localeCompare(b.type)
          : b.type.localeCompare(a.type);
      } else if (field === 'fsType') {
        return direction === 'asc'
          ? a.fsType.localeCompare(b.fsType)
          : b.fsType.localeCompare(a.fsType);
      } else if (field === 'nwcRole') {
        // Handle cases where nwcRole might be undefined
        const roleA = a.nwcRole || '';
        const roleB = b.nwcRole || '';
        
        return direction === 'asc'
          ? roleA.localeCompare(roleB)
          : roleB.localeCompare(roleA);
      } else if (field === 'ebitdaRole') {
        // Handle cases where ebitdaRole might be undefined
        const roleA = a.ebitdaRole || '';
        const roleB = b.ebitdaRole || '';
        
        return direction === 'asc'
          ? roleA.localeCompare(roleB)
          : roleB.localeCompare(roleA);
      } else if (field === 'tagsInclude') {
        // Sort by the first tag in the include array, or empty string if no tags
        const tagsA = (a.tagsInclude || []).join(', ');
        const tagsB = (b.tagsInclude || []).join(', ');
        
        return direction === 'asc'
          ? tagsA.localeCompare(tagsB)
          : tagsB.localeCompare(tagsA);
      } else if (field === 'tagsExclude') {
        // Sort by the first tag in the exclude array, or empty string if no tags
        const tagsA = (a.tagsExclude || []).join(', ');
        const tagsB = (b.tagsExclude || []).join(', ');
        
        return direction === 'asc'
          ? tagsA.localeCompare(tagsB)
          : tagsB.localeCompare(tagsA);
      }
      
      // Default sorting by financial statement order
      return sortCategoriesByFinancialOrder(a, b);
    });
  }, [categories, filters, sortConfig]);

  // Get unique values for filter dropdowns
  const uniqueValues = useMemo(() => {
    const labels = [...new Set(categories.map(c => c.label))];
    const types = [...new Set(categories.map(c => formatCategoryType(c.type)))];
    const fsTypes = ['Balance Sheet', 'Income Statement'];
    
    return {
      label: labels,
      type: types,
      fsType: fsTypes
    };
  }, [categories]);

  // Filter popover component
  const FilterPopover = ({ column }: { column: keyof Filters }) => {
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
          setActiveFilterColumn(null);
        }
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    if (activeFilterColumn !== column) return null;

    const options = uniqueValues[column as keyof typeof uniqueValues];

    return (
      <div 
        ref={popoverRef}
        className="absolute mt-2 bg-white shadow-lg rounded-lg p-4 z-40 border min-w-[240px]"
        style={{ 
          left: column === 'label' ? '0' : 'auto',
          right: column === 'fsType' ? '0' : 'auto',
          top: '100%'
        }}
      >
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={filters[column].enabled}
            onChange={() => handleFilterToggle(column)}
            className="rounded border-gray-300"
          />
          <ComboboxSelect
            value={filters[column].value}
            onChange={(value) => handleFilterChange(column, value)}
            options={options}
            placeholder={`Filter ${column}...`}
            onToggle={() => handleFilterToggle(column)}
          />
        </div>
      </div>
    );
  };

  const handleEditStart = (category: CategoryOption) => {
    // Check if the template is default (read-only)
    if (editingTemplate?.is_default) {
      setNotification({
        show: true,
        message: 'System Default Template cannot be edited. Please make a copy instead.',
        type: 'error'
      });
      return;
    }
    
    // Store original state for potential cancellation
    setOriginalCategoryState({...category});
    setEditingId(category.value);
  };

  const handleEditCancel = () => {
    // Revert any changes made to the category being edited
    if (originalCategoryState) {
      const updatedCategories = categories.map(c => 
        c.value === originalCategoryState.value ? originalCategoryState : c
      );
      setCategories(updatedCategories);
    }
    
    // Exit edit mode
    setEditingId(null);
    setOriginalCategoryState(null);
  };

  const handleSave = async (category: CategoryOption) => {
    if (!editingTemplate) return;
    
    // Check if editing a default template
    if (editingTemplate.is_default) {
      setNotification({
        show: true,
        message: 'Cannot modify the System Default Template',
        type: 'error'
      });
      return;
    }

    try {
      const updatedCategories = categories.map(c => 
        c.value === category.value ? category : c
      );
      
      await templateApi.update(editingTemplate.id, { 
        categories: updatedCategories,
        updated_at: new Date().toISOString()
      });

      setCategories(updatedCategories);
      
      // If we're editing the active template, update it
      if (activeTemplate?.id === editingTemplate.id) {
        onUpdateCategories(updatedCategories);
      }
      
      setEditingId(null);
      setOriginalCategoryState(null);
      
      // Show success notification
      setNotification({
        show: true,
        message: 'Category saved successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error saving category:', error);
      setNotification({
        show: true,
        message: 'Failed to save category changes',
        type: 'error'
      });
    }
  };

  const handleDelete = async (categoryValue: string) => {
    if (!editingTemplate) return;
    
    // Check if editing a default template
    if (editingTemplate.is_default) {
      setNotification({
        show: true,
        message: 'Cannot modify the System Default Template',
        type: 'error'
      });
      return;
    }

    try {
      const updatedCategories = categories.filter(c => c.value !== categoryValue);
      
      await templateApi.update(editingTemplate.id, { 
        categories: updatedCategories,
        updated_at: new Date().toISOString()
      });

      setCategories(updatedCategories);
      
      // If we're editing the active template, update it
      if (activeTemplate?.id === editingTemplate.id) {
        onUpdateCategories(updatedCategories);
      }
      
      // Show success notification
      setNotification({
        show: true,
        message: 'Category deleted successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      setNotification({
        show: true,
        message: 'Failed to delete category',
        type: 'error'
      });
    }
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-full py-2 align-middle">
        <TemplateManagement
          templates={templates}
          activeTemplate={activeTemplate}
          editingTemplate={editingTemplate}
          onSetActiveTemplate={handleSetActiveTemplate}
          onSelectTemplateForEditing={handleSelectTemplateForEditing}
          onTemplateCopy={handleTemplateCopy}
          onTemplateDelete={handleTemplateDelete}
          onCreateTemplate={handleCreateTemplate}
          onRenameTemplate={handleRenameTemplate}
        />
        
        {/* Categories section with collapsible legends */}
        {editingTemplate && (
          <>
            {/* Header with Add Category Button, Default Template Warning, and Legend Toggle */}
            <div className="flex items-center justify-between mt-8 mb-4">
              <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                {editingTemplate.is_default && (
                  <Lock className="h-4 w-4 text-amber-600 mr-2" />
                )}
                {editingTemplate.is_default ? (
                  // For System Default Template, just show the template name without "Editing Categories:" prefix
                  editingTemplate.name
                ) : (
                  // For user templates, keep the "Editing Categories:" prefix
                  `Editing Categories: ${editingTemplate.name}`
                )}
                {editingTemplate.is_default && (
                  <span className="ml-2 text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full">
                    Read-only
                  </span>
                )}
              </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {editingTemplate.is_default 
                    ? ""
                    : "Edit existing category names and mappings by clicking 'Edit' in the 'Actions' column in the table. You can also add a new category by clicking 'Add Category' to the right"}
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowLegends(!showLegends)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  {showLegends ? 'Hide Legends' : 'Show Legends'}
                </button>
                
                {editingTemplate.is_default ? (
                  <button
                    onClick={() => handleTemplateCopy(editingTemplate)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Template
                  </button>
                ) : (
                  <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </button>
                )}
              </div>
            </div>
            
            {/* Default template warning banner */}
            {editingTemplate.is_default && (
              <div className="mb-4 bg-amber-50 border-l-4 border-amber-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-amber-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">System Default Template - Read Only</h3>
                    <div className="mt-2 text-sm text-amber-700">
                      <p>
                        System Default Template cannot be modified. You can copy and modify this template to customize categories; click the "Copy Template" button to create your own editable version.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Collapsible legends section */}
            {showLegends && (
              <div className="mb-6">
                <CollapsibleLegend 
                  title="Net Working Capital (NWC) Roles" 
                  icon={<Info className="h-5 w-5 text-blue-500" />}
                >
                  <NWCRoleLegend />
                </CollapsibleLegend>
                
                <CollapsibleLegend 
                  title="EBITDA Components" 
                  icon={<Info className="h-5 w-5 text-purple-500" />}
                >
                  <EBITDARoleLegend />
                </CollapsibleLegend>
              </div>
            )}
            
            <div className="mb-4 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Showing {filteredAndSortedCategories.length} of {categories.length} categories
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={resetToOriginalOrder}
                  className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset to Default Order
                </button>
                
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
            
            <div className="sticky-table-wrapper" ref={tableContainerRef}>
              {activeFilterCount > 0 && (
                <div className="bg-blue-50 px-4 py-2 flex items-center justify-between mb-0 rounded-t-md border border-blue-200 border-b-0">
                  <div className="text-sm text-blue-700 flex items-center">
                    <Filter className="h-4 w-4 mr-2" />
                    <span>{activeFilterCount} active filter{activeFilterCount !== 1 ? 's' : ''}</span>
                  </div>
                  <button 
                    onClick={clearFilters}
                    className="text-xs text-blue-700 hover:text-blue-900"
                  >
                    Clear all
                  </button>
                </div>
              )}
              
              <table className="sticky-table min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    {/* Category Name column */}
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell sticky-column sticky-category-name">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSort('label')}
                          className="flex items-center gap-1 hover:text-blue-600"
                        >
                          <span>Category Name</span>
                          {renderSortIcon('label')}
                        </button>
                        <button
                          onClick={() => setActiveFilterColumn(activeFilterColumn === 'label' ? null : 'label')}
                          className={`hover:bg-gray-100 p-1 rounded ${
                            filters.label.enabled ? 'text-blue-600' : 'text-gray-400'
                          }`}
                        >
                          <Filter className="w-4 h-4" />
                        </button>
                      </div>
                      <FilterPopover column="label" />
                    </th>
                    
                    {/* Category Type column */}
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSort('type')}
                          className="flex items-center gap-1 hover:text-blue-600"
                        >
                          <span>Category Type</span>
                          {renderSortIcon('type')}
                        </button>
                        <button
                          onClick={() => setActiveFilterColumn(activeFilterColumn === 'type' ? null : 'type')}
                          className={`hover:bg-gray-100 p-1 rounded ${
                            filters.type.enabled ? 'text-blue-600' : 'text-gray-400'
                          }`}
                        >
                          <Filter className="w-4 h-4" />
                        </button>
                      </div>
                      <FilterPopover column="type" />
                    </th>
                    
                    {/* Statement Type column */}
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSort('fsType')}
                          className="flex items-center gap-1 hover:text-blue-600"
                        >
                          <span>Statement Type</span>
                          {renderSortIcon('fsType')}
                        </button>
                        <button
                          onClick={() => setActiveFilterColumn(activeFilterColumn === 'fsType' ? null : 'fsType')}
                          className={`hover:bg-gray-100 p-1 rounded ${
                            filters.fsType.enabled ? 'text-blue-600' : 'text-gray-400'
                          }`}
                        >
                          <Filter className="w-4 h-4" />
                        </button>
                      </div>
                      <FilterPopover column="fsType" />
                    </th>
                    
                    {/* NWC Role column */}
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSort('nwcRole')}
                          className="flex items-center gap-1 hover:text-blue-600"
                        >
                          <span>NWC Role</span>
                          {renderSortIcon('nwcRole')}
                        </button>
                      </div>
                    </th>
                    
                    {/* EBITDA Role column */}
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSort('ebitdaRole')}
                          className="flex items-center gap-1 hover:text-blue-600"
                        >
                          <span>EBITDA Role</span>
                          {renderSortIcon('ebitdaRole')}
                        </button>
                      </div>
                    </th>
                    
                    {/* Non-cash? column */}
                    <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 sticky-header-cell">Non-cash?</th>
                    
                    {/* PPE? column */}
                    <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 sticky-header-cell">PPE?</th>
                    
                    {/* Debt? column */}
                    <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 sticky-header-cell">Debt?</th>
                    
                    {/* Equity Capital? column */}
                    <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 sticky-header-cell">Equity Capital?</th>
                    
                    {/* Tags (Include) column */}
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSort('tagsInclude')}
                          className="flex items-center gap-1 hover:text-blue-600"
                        >
                          <span>Tags (Include)</span>
                          {renderSortIcon('tagsInclude')}
                        </button>
                      </div>
                    </th>
                    
                    {/* Tags (Exclude) column */}
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 sticky-header-cell">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSort('tagsExclude')}
                          className="flex items-center gap-1 hover:text-blue-600"
                        >
                          <span>Tags (Exclude)</span>
                          {renderSortIcon('tagsExclude')}
                        </button>
                      </div>
                    </th>
                    
                    {/* Actions column */}
                    <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900 sticky-header-cell actions-column">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredAndSortedCategories.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-6 py-10 text-center text-sm text-gray-500">
                        <div className="flex flex-col items-center">
                          <Filter className="h-10 w-10 text-gray-300 mb-2" />
                          <p className="font-medium">No categories found</p>
                          <p className="text-xs text-gray-400 mt-1">Try adjusting your filters or search terms</p>
                          {activeFilterCount > 0 && (
                            <button 
                              onClick={clearFilters}
                              className="mt-3 px-3 py-1 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
                            >
                              Clear all filters
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredAndSortedCategories.map((category) => (
                      <tr 
                        key={category.value}
                        className={newlyAddedCategory === category.value ? 'bg-green-50 transition-colors duration-500' : 'hover:bg-gray-50'}
                      >
                        {/* Category Name column - sticky */}
                        <td className="px-6 py-4 text-sm text-gray-900 sticky-column sticky-category-name">
                          {newlyAddedCategory === category.value && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mr-2">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              New
                            </span>
                          )}
                          {editingId === category.value ? (
                            <input
                              type="text"
                              value={category.label}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                const updatedCategories = categories.map(c =>
                                  c.value === category.value ? { 
                                    ...c, 
                                    label: newValue
                                  } : c
                                );
                                setCategories(updatedCategories);
                              }}
                              className="rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                          ) : (
                            <div className="max-w-[250px] truncate">{category.label}</div>
                          )}
                        </td>
                        
                        {/* Category Type column */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {editingId === category.value ? (
                            <select
                              value={category.type}
                              onChange={(e) => {
                                const newType = e.target.value as AccountType;
                                const updatedCategories = categories.map(c =>
                                  c.value === category.value ? { 
                                    ...c, 
                                    type: newType,
                                    fsType: getFSType(newType)
                                  } : c
                                );
                                setCategories(updatedCategories);
                              }}
                              className="rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              disabled={editingTemplate?.is_default}
                            >
                              {accountTypes.map(type => (
                                <option key={type} value={type}>
                                  {formatCategoryType(type)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            formatCategoryType(category.type)
                          )}
                        </td>
                        
                        {/* Statement Type column */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {category.fsType === 'balance-sheet' ? 'Balance Sheet' : 'Income Statement'}
                        </td>
                                                  
                        {/* NWC Role column */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {editingId === category.value ? (
                            // When editing, show the appropriate selector or message
                            <NWCRoleSelector
                              value={category.nwcRole || null}
                              onChange={(newRole) => {
                                const updatedCategories = categories.map(c =>
                                  c.value === category.value ? { 
                                    ...c, 
                                    nwcRole: newRole
                                  } : c
                                );
                                setCategories(updatedCategories);
                              }}
                              accountType={category.type}
                            />
                          ) : (
                            // When not editing, show role badge or indicator
                            <>
                              {category.nwcRole ? (
                                // Show badge with role if one is assigned
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getNWCRoleColorClass(category.nwcRole)}`}>
                                  {category.nwcRole.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                  {isDefinitionalAdjustment(category.nwcRole) && (
                                    <span className="ml-1">(Adj)</span>
                                  )}
                                  {isTradeWorkingCapital(category.nwcRole) && (
                                    <span className="ml-1">(Trade)</span>
                                  )}
                                </span>
                              ) : (
                                // Show appropriate indicator based on account type
                                ['current-asset', 'current-liability'].includes(category.type) ? (
                                  <span className="text-gray-400">—</span>
                                ) : (
                                  <span className="text-gray-400 text-xs">N/A</span>
                                )
                              )}
                            </>
                          )}
                        </td>

                        {/* EBITDA Role column */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {editingId === category.value ? (
                            // When editing, show the appropriate selector or message
                            <EBITDARoleSelector
                              value={category.ebitdaRole || null}
                              onChange={(newRole) => {
                                const updatedCategories = categories.map(c =>
                                  c.value === category.value ? { 
                                    ...c, 
                                    ebitdaRole: newRole
                                  } : c
                                );
                                setCategories(updatedCategories);
                              }}
                              accountType={category.type}
                            />
                          ) : (
                            // When not editing, show role badge or indicator
                            <>
                              {category.ebitdaRole ? (
                                // Show badge with role if one is assigned
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getEBITDARoleColorClass(category.ebitdaRole)}`}>
                                  {getEBITDADisplayLabel(category.ebitdaRole)}
                                </span>
                              ) : (
                                // Show appropriate indicator based on account type
                                ['operating-expense', 'interest-expense', 'income-tax-expense'].includes(category.type) ? (
                                  <span className="text-gray-400">—</span>
                                ) : (
                                  <span className="text-gray-400 text-xs">N/A</span>
                                )
                              )}
                            </>
                          )}
                        </td>
                        
                        {/* Non-cash? column */}
                        <td className="px-6 py-4 text-center">
                          {editingId === category.value ? (
                            <input type="checkbox" checked={category.isNonCash || false} onChange={e => {
                              const updatedCategories = categories.map(c =>
                                c.value === category.value ? { ...c, isNonCash: e.target.checked } : c
                              );
                              setCategories(updatedCategories);
                            }} />
                          ) : (
                            category.isNonCash ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : null
                          )}
                        </td>
                        
                        {/* PPE? column */}
                        <td className="px-6 py-4 text-center">
                          {editingId === category.value ? (
                            <input type="checkbox" checked={category.isPpe || false} onChange={e => {
                              const updatedCategories = categories.map(c =>
                                c.value === category.value ? { ...c, isPpe: e.target.checked } : c
                              );
                              setCategories(updatedCategories);
                            }} />
                          ) : (
                            category.isPpe ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : null
                          )}
                        </td>
                        
                        {/* Debt? column */}
                        <td className="px-6 py-4 text-center">
                          {editingId === category.value ? (
                            <input type="checkbox" checked={category.isDebt || false} onChange={e => {
                              const updatedCategories = categories.map(c =>
                                c.value === category.value ? { ...c, isDebt: e.target.checked } : c
                              );
                              setCategories(updatedCategories);
                            }} />
                          ) : (
                            category.isDebt ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : null
                          )}
                        </td>
                        
                        {/* Equity Capital? column */}
                        <td className="px-6 py-4 text-center">
                          {editingId === category.value ? (
                            <input type="checkbox" checked={category.isEquityCapital || false} onChange={e => {
                              const updatedCategories = categories.map(c =>
                                c.value === category.value ? { ...c, isEquityCapital: e.target.checked } : c
                              );
                              setCategories(updatedCategories);
                            }} />
                          ) : (
                            category.isEquityCapital ? <CheckCircle className="h-4 w-4 text-green-500 mx-auto" /> : null
                          )}
                        </td>
                        
                        {/* Tags (Include) column */}
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {editingId === category.value ? (
                            <input
                              type="text"
                              value={(category.tagsInclude || []).join(', ')}
                              onChange={(e) => {
                                const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                                const updatedCategories = categories.map(c =>
                                  c.value === category.value ? { ...c, tagsInclude: tags } : c
                                );
                                setCategories(updatedCategories);
                              }}
                              className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter tags separated by commas"
                            />
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(category.tagsInclude || []).map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        
                        {/* Tags (Exclude) column */}
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {editingId === category.value ? (
                            <input
                              type="text"
                              value={(category.tagsExclude || []).join(', ')}
                              onChange={(e) => {
                                const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                                const updatedCategories = categories.map(c =>
                                  c.value === category.value ? { ...c, tagsExclude: tags } : c
                                );
                                setCategories(updatedCategories);
                              }}
                              className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Enter tags separated by commas"
                            />
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(category.tagsExclude || []).map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        
                        {/* Actions column */}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {editingId === category.value ? (
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleSave(category)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleEditCancel}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end space-x-2">
                              {editingTemplate.is_default ? (
                                <span className="text-gray-400 flex items-center">
                                  <Lock className="h-4 w-4 mr-1" />
                                  Read-only
                                </span>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleEditStart(category)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDelete(category.value)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
        
        {/* Add Category Modal */}
        {isAddModalOpen && editingTemplate && (
          <AddCategoryModal
            onClose={() => setIsAddModalOpen(false)}
            onAdd={handleAddCategory}
            accountTypes={accountTypes}
            getFSType={getFSType}
            detectNWCRole={detectNWCRole}
          />
        )}
        
        {/* Success/Error notification */}
        {notification.show && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification({ ...notification, show: false })}
          />
        )}
      </div>
    </div>
  );
}
